import type { SoccerFormat, SoccerTeamId, SoccerVariantId } from "../../../lib/multiplayer/protocol";
import {
  SOCCER_TEAM_BLUE,
  SOCCER_TEAM_ORANGE,
  SOCCER_VARIANT_MEGA,
  SOCCER_VARIANT_NORMAL,
  type SoccerTeam,
} from "../../../lib/multiplayer/soccer-sim";
import { isSoccerMobileView } from "./view";
import { claimExclusiveUi } from "../exclusive-ui";

export type SoccerUiHooks = {
  showEl: (el: HTMLElement | null) => void;
  hideEl: (el: HTMLElement | null) => void;
  openGameHub: () => void;
  playModeCardBeep: () => void;
  playScreenEnterMotion: () => void;
  onCompeteFormat: (format: SoccerFormat, variant: SoccerVariantId) => void;
};

let hooks: SoccerUiHooks | null = null;

const mount = () => document.getElementById("soccerScreenMount");
const screen = () => document.getElementById("soccerScreen");
const overlay = () => document.getElementById("soccerOverlay");
const menuBtn = () => document.getElementById("soccerMenuBtn");

export function mountSoccerUi(h: SoccerUiHooks): void {
  hooks = h;
  ensureOverlay();
  if (!document.getElementById("soccerMenuBtn")) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.id = "soccerMenuBtn";
    btn.className = "soccerMenuBtn hidden";
    btn.textContent = "MENU";
    document.body.appendChild(btn);
  }
}

export function hideSoccerScreens(): void {
  hooks?.hideEl(screen());
  hideOverlay();
  hideSoccerMenuBtn();
}

export function isSoccerScreenVisible(): boolean {
  const el = screen();
  return !!el && !el.classList.contains("hidden");
}

export function openSoccerCompete(): void {
  if (!hooks) return;
  claimExclusiveUi("soccerScreen");
  hooks.hideEl(document.getElementById("modeScreen"));
  document.getElementById("soccerScreen")?.classList.remove("hidden");
  const sc = screen();
  if (sc) {
    sc.classList.remove("hidden");
    hooks.showEl(sc);
  }
  renderCompeteHome();
  hooks.showEl(document.getElementById("returnBtn"));
  hooks.playScreenEnterMotion();
}

function renderCompeteHome(): void {
  const m = mount();
  if (!m) return;
  m.innerHTML = `
    <h1>CURSOR SOCCER</h1>
    <p class="accessHint">2–3 minute matches. Pick Normal or Mega Field. Random teams — golden goal on ties.</p>
    <div class="soccerHomeArt" aria-hidden="true">
      <span class="soccerHomeHalf soccerHomeHalf--blue"></span>
      <span class="soccerHomeHalf soccerHomeHalf--orange"></span>
    </div>
    <button type="button" class="btn green soccerPlayBtn" id="soccerPlayBtn">PLAY</button>
  `;
  m.querySelector("#soccerPlayBtn")?.addEventListener("click", () => {
    hooks?.playModeCardBeep();
    renderFormatSelect();
  });
}

export function renderFormatSelect(
  onReady?: (format: SoccerFormat, variant: SoccerVariantId) => void,
): void {
  const m = mount();
  if (!m) return;
  showScreen();
  m.innerHTML = `
    <h1>CURSOR SOCCER</h1>
    <p class="accessHint">Choose a match size. Teams are assigned randomly.</p>
    <div class="soccerFormatGrid">
      <button type="button" class="modeBox soccerFormatCard" data-format="1v1"><h3>1v1</h3><p>One vs one. Blue vs orange.</p></button>
      <button type="button" class="modeBox soccerFormatCard" data-format="2v2"><h3>2v2</h3><p>Two vs two. Random sides.</p></button>
    </div>
  `;
  m.querySelectorAll<HTMLElement>("[data-format]").forEach((card) => {
    card.addEventListener("click", () => {
      hooks?.playModeCardBeep();
      const format = card.getAttribute("data-format") as SoccerFormat;
      renderVariantSelect(format, (variant) => {
        const fn = onReady ?? hooks?.onCompeteFormat;
        fn?.(format, variant);
      });
    });
  });
}

export function soccerVariantLabel(variant: SoccerVariantId): string {
  return variant === "mega" ? SOCCER_VARIANT_MEGA.label : SOCCER_VARIANT_NORMAL.label;
}

export function renderVariantSelect(format: SoccerFormat, onPick: (variant: SoccerVariantId) => void): void {
  const m = mount();
  if (!m) return;
  showScreen();
  m.innerHTML = `
    <h1>FIELD SIZE</h1>
    <p class="accessHint">${format} — choose the pitch variant.</p>
    <div class="soccerVariantGrid">
      <button type="button" class="modeBox soccerVariantCard soccerVariantCard--normal" data-variant="normal">
        <h3>${SOCCER_VARIANT_NORMAL.label}</h3>
        <p>Classic pitch. Direct cursor control. 2-minute matches.</p>
      </button>
      <button type="button" class="modeBox soccerVariantCard soccerVariantCard--mega" data-variant="mega">
        <h3>${SOCCER_VARIANT_MEGA.label}</h3>
        <p>Huge stadium. Speed chase, boost, pickups. 3-minute matches.</p>
      </button>
    </div>
  `;
  m.querySelectorAll<HTMLElement>("[data-variant]").forEach((card) => {
    card.addEventListener("click", () => {
      hooks?.playModeCardBeep();
      onPick(card.getAttribute("data-variant") as SoccerVariantId);
    });
  });
}

export function renderSoccerQueue(
  format: SoccerFormat,
  variant: SoccerVariantId,
  searching: number,
  onCancel: () => void,
): void {
  const m = mount();
  if (!m) return;
  showScreen();
  m.innerHTML = `
    <h1>SEARCHING</h1>
    <p class="accessHint soccerQueueCount">${searching} player${searching === 1 ? "" : "s"} looking for ${format} · ${soccerVariantLabel(variant)}</p>
    <div class="soccerQueuePulse" aria-hidden="true"></div>
    <button type="button" class="btn" id="soccerCancelQueueBtn">Cancel</button>
  `;
  m.querySelector("#soccerCancelQueueBtn")?.addEventListener("click", () => {
    hooks?.playModeCardBeep();
    onCancel();
  });
}

export function updateSoccerQueueCount(
  format: SoccerFormat,
  variant: SoccerVariantId,
  searching: number,
): void {
  const el = mount()?.querySelector(".soccerQueueCount");
  if (el) {
    el.textContent = `${searching} player${searching === 1 ? "" : "s"} looking for ${format} · ${soccerVariantLabel(variant)}`;
  }
}

export function renderSoccerHostVariant(
  current: SoccerVariantId,
  onPick: (variant: SoccerVariantId) => void,
): void {
  showOverlay();
  const ov = overlay();
  if (!ov) return;
  const panel = ov.querySelector(".soccerPanel");
  if (!panel) return;
  let bar = ov.querySelector(".soccerHostVariantBar") as HTMLElement | null;
  if (!bar) {
    bar = document.createElement("div");
    bar.className = "soccerHostVariantBar";
    panel.insertBefore(bar, panel.firstChild);
  }
  bar.innerHTML = `
    <p class="accessHint">Host: field variant</p>
    <div class="soccerVariantPickRow">
      <button type="button" class="btn ${current === "normal" ? "green" : ""}" data-v="normal">NORMAL</button>
      <button type="button" class="btn ${current === "mega" ? "green" : ""}" data-v="mega">MEGA FIELD</button>
    </div>
  `;
  bar.querySelectorAll<HTMLButtonElement>("[data-v]").forEach((btn) => {
    btn.addEventListener("click", () => {
      hooks?.playModeCardBeep();
      onPick(btn.getAttribute("data-v") as SoccerVariantId);
    });
  });
}

export type SoccerTeamSelectPlayer = {
  id: string;
  name: string;
  team: SoccerTeam | null;
  isLocal?: boolean;
};

export function renderTeamSelect(opts: {
  players: SoccerTeamSelectPlayer[];
  hostCanStart?: boolean;
  perPlayer?: boolean;
  onPick: (team: SoccerTeam | "random") => void;
  onPickPlayer?: (playerId: string, team: SoccerTeam | "random") => void;
  onStart?: () => void;
}): void {
  showOverlay();
  const ov = overlay();
  if (!ov) return;
  const blues = opts.players.filter((p) => p.team === "blue");
  const oranges = opts.players.filter((p) => p.team === "orange");
  const roster = opts.perPlayer
    ? `<div class="soccerPlayerTeamList">${opts.players
        .map(
          (p) => `<div class="soccerPlayerTeamRow">
          <span>${escapeHtml(p.name)}</span>
          <button type="button" class="btn soccerBlueBtn ${p.team === "blue" ? "green" : ""}" data-pid="${escapeHtml(p.id)}" data-team="blue">BLUE</button>
          <button type="button" class="btn soccerOrangeBtn ${p.team === "orange" ? "green" : ""}" data-pid="${escapeHtml(p.id)}" data-team="orange">ORANGE</button>
        </div>`,
        )
        .join("")}</div>`
    : "";
  ov.innerHTML = `
    <div class="soccerPanel soccerTeamSelect">
      <h2>SELECT TEAM</h2>
      <div class="soccerTeamCols">
        <div class="soccerTeamCol soccerTeamCol--blue">
          <h3>TEAM BLUE</h3>
          <ul>${blues.map((p) => `<li>${escapeHtml(p.name)}</li>`).join("") || "<li class='dim'>Empty</li>"}</ul>
        </div>
        <div class="soccerTeamCol soccerTeamCol--orange">
          <h3>TEAM ORANGE</h3>
          <ul>${oranges.map((p) => `<li>${escapeHtml(p.name)}</li>`).join("") || "<li class='dim'>Empty</li>"}</ul>
        </div>
      </div>
      ${roster}
      <div class="soccerTeamActions">
        ${
          opts.perPlayer
            ? `<button type="button" class="btn" data-team="random">RANDOM</button>`
            : `<button type="button" class="btn soccerBlueBtn" data-team="blue">TEAM BLUE</button>
               <button type="button" class="btn soccerOrangeBtn" data-team="orange">TEAM ORANGE</button>
               <button type="button" class="btn" data-team="random">RANDOM</button>`
        }
      </div>
      ${opts.hostCanStart ? `<button type="button" class="btn green" id="soccerTeamStartBtn">START</button>` : `<p class="accessHint">Waiting for host…</p>`}
    </div>
  `;
  ov.querySelectorAll<HTMLButtonElement>("[data-team]").forEach((btn) => {
    btn.addEventListener("click", () => {
      hooks?.playModeCardBeep();
      const team = btn.getAttribute("data-team") as SoccerTeam | "random";
      const pid = btn.getAttribute("data-pid");
      if (pid && opts.onPickPlayer) opts.onPickPlayer(pid, team);
      else opts.onPick(team);
    });
  });
  ov.querySelector("#soccerTeamStartBtn")?.addEventListener("click", () => {
    hooks?.playModeCardBeep();
    opts.onStart?.();
  });
}

export function renderSoccerPause(opts: {
  allowTeamChange: boolean;
  localRoster?: SoccerTeamSelectPlayer[];
  onResume: () => void;
  onChangeTeam?: (team: SoccerTeamId, playerId?: string) => void;
  onLeave: () => void;
}): void {
  showOverlay();
  const ov = overlay();
  if (!ov) return;
  const localRows =
    opts.allowTeamChange && opts.localRoster?.length
      ? opts.localRoster
          .map(
            (p) => `<div class="soccerPlayerTeamRow">
              <span>${escapeHtml(p.name)}</span>
              <button type="button" class="btn soccerBlueBtn" data-chg="blue" data-pid="${escapeHtml(p.id)}">BLUE</button>
              <button type="button" class="btn soccerOrangeBtn" data-chg="orange" data-pid="${escapeHtml(p.id)}">ORANGE</button>
            </div>`,
          )
          .join("")
      : `<button type="button" class="btn soccerBlueBtn" data-chg="blue">BLUE</button>
         <button type="button" class="btn soccerOrangeBtn" data-chg="orange">ORANGE</button>`;
  ov.innerHTML = `
    <div class="soccerPanel soccerPause">
      <h2>MENU</h2>
      <button type="button" class="btn green" id="soccerResumeBtn">RESUME</button>
      ${
        opts.allowTeamChange
          ? `<button type="button" class="btn" id="soccerChangeTeamBtn">CHANGE TEAM</button>
             <div id="soccerChangeTeamRow" class="soccerChangeTeamRow hidden">${localRows}</div>`
          : ""
      }
      <button type="button" class="btn soccerLeaveBtn" id="soccerLeaveBtn">LEAVE</button>
    </div>
  `;
  ov.querySelector("#soccerResumeBtn")?.addEventListener("click", () => {
    hooks?.playModeCardBeep();
    hideOverlay();
    opts.onResume();
  });
  ov.querySelector("#soccerChangeTeamBtn")?.addEventListener("click", () => {
    hooks?.playModeCardBeep();
    ov.querySelector("#soccerChangeTeamRow")?.classList.toggle("hidden");
  });
  ov.querySelectorAll<HTMLButtonElement>("[data-chg]").forEach((btn) => {
    btn.addEventListener("click", () => {
      hooks?.playModeCardBeep();
      opts.onChangeTeam?.(btn.getAttribute("data-chg") as SoccerTeamId, btn.getAttribute("data-pid") ?? undefined);
      hideOverlay();
    });
  });
  ov.querySelector("#soccerLeaveBtn")?.addEventListener("click", () => {
    hooks?.playModeCardBeep();
    hideOverlay();
    opts.onLeave();
  });
}

export type SoccerWinnerCard = {
  name: string;
  team: SoccerTeam;
  dots: number;
  tournamentsWon: number;
  soccerWins: number;
  photoUrl?: string | null;
  bannerUrl?: string | null;
  cursorColor: string;
};

export type SoccerStatRow = {
  id: string;
  name: string;
  team: SoccerTeam;
  goals: number;
  shots: number;
  saves: number;
  touches: number;
  posts: number;
};

export function renderSoccerWinnerThenResults(opts: {
  winner: SoccerWinnerCard;
  stats: SoccerStatRow[];
  onPlayAgain: () => void;
  onRematch: () => void;
  onLeave: () => void;
  rematchHint?: string;
}): void {
  showOverlay();
  const ov = overlay();
  if (!ov) return;
  ov.innerHTML = `
    <div class="soccerPanel soccerWinner">
      <div class="soccerWinnerBanner" style="${opts.winner.bannerUrl ? `background-image:url('${opts.winner.bannerUrl}')` : `background:${opts.winner.team === "blue" ? SOCCER_TEAM_BLUE : SOCCER_TEAM_ORANGE}`}"></div>
      <div class="soccerWinnerPhoto" style="background:${opts.winner.cursorColor}">
        ${opts.winner.photoUrl ? `<img src="${opts.winner.photoUrl}" alt="">` : ""}
      </div>
      <h2>${escapeHtml(opts.winner.name)}</h2>
      <p class="soccerWinnerTeam">${opts.winner.team === "blue" ? "TEAM BLUE" : "TEAM ORANGE"} WINS</p>
      <ul class="soccerWinnerMeta">
        <li>Wins ${opts.winner.soccerWins}</li>
        <li>Tournaments ${opts.winner.tournamentsWon}</li>
        <li>Dots ${opts.winner.dots}</li>
      </ul>
    </div>
  `;
  window.setTimeout(() => {
    if (!overlay() || overlay() !== ov) return;
    ov.innerHTML = `
      <div class="soccerPanel soccerResults">
        <h2>MATCH STATS</h2>
        <table class="soccerStatsTable">
          <thead><tr><th></th><th>G</th><th>SOG</th><th>SV</th><th>TCH</th><th>PST</th></tr></thead>
          <tbody>
            ${opts.stats
              .map(
                (s) => `<tr class="soccerStatsRow--${s.team}">
                  <td>${escapeHtml(s.name)}</td><td>${s.goals}</td><td>${s.shots}</td><td>${s.saves}</td><td>${s.touches}</td><td>${s.posts}</td>
                </tr>`,
              )
              .join("")}
          </tbody>
        </table>
        <p class="accessHint soccerRematchHint">${opts.rematchHint ? escapeHtml(opts.rematchHint) : ""}</p>
        <div class="soccerResultsActions">
          <button type="button" class="btn green" id="soccerPlayAgainBtn">PLAY AGAIN</button>
          <button type="button" class="btn" id="soccerRematchBtn">REMATCH</button>
          <button type="button" class="btn soccerLeaveBtn" id="soccerResultLeaveBtn">LEAVE</button>
        </div>
      </div>
    `;
    ov.querySelector("#soccerPlayAgainBtn")?.addEventListener("click", () => {
      hooks?.playModeCardBeep();
      hideOverlay();
      opts.onPlayAgain();
    });
    ov.querySelector("#soccerRematchBtn")?.addEventListener("click", () => {
      hooks?.playModeCardBeep();
      opts.onRematch();
    });
    ov.querySelector("#soccerResultLeaveBtn")?.addEventListener("click", () => {
      hooks?.playModeCardBeep();
      hideOverlay();
      opts.onLeave();
    });
  }, 3000);
}

export function setSoccerRematchHint(text: string): void {
  const el = overlay()?.querySelector(".soccerRematchHint");
  if (el) el.textContent = text;
}

export function showSoccerMenuBtn(onClick: () => void): void {
  const btn = menuBtn();
  if (!btn) return;
  btn.classList.remove("hidden");
  btn.classList.toggle("soccerMenuBtn--mobile", isSoccerMobileView());
  btn.onclick = () => {
    hooks?.playModeCardBeep();
    onClick();
  };
}

export function hideSoccerMenuBtn(): void {
  menuBtn()?.classList.add("hidden");
}

export function showSoccerToast(text: string): void {
  let t = document.getElementById("soccerToast");
  if (!t) {
    t = document.createElement("div");
    t.id = "soccerToast";
    t.className = "soccerToast";
    document.body.appendChild(t);
  }
  t.textContent = text;
  t.classList.remove("hidden");
  window.setTimeout(() => t?.classList.add("hidden"), 2800);
}

function showScreen(): void {
  const sc = screen();
  if (sc) {
    sc.classList.remove("hidden");
    hooks?.showEl(sc);
  }
}

let replaySkipBtn: HTMLButtonElement | null = null;

export function showSoccerReplaySkipBtn(onSkip: () => void): void {
  if (!replaySkipBtn) {
    replaySkipBtn = document.createElement("button");
    replaySkipBtn.type = "button";
    replaySkipBtn.id = "soccerReplaySkipBtn";
    replaySkipBtn.className = "soccerReplaySkipBtn hidden";
    replaySkipBtn.textContent = "Skip";
    document.body.appendChild(replaySkipBtn);
  }
  replaySkipBtn.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSkip();
  };
  replaySkipBtn.classList.remove("hidden");
}

export function hideSoccerReplaySkipBtn(): void {
  replaySkipBtn?.classList.add("hidden");
}

function ensureOverlay(): HTMLElement {
  let ov = overlay();
  if (!ov) {
    ov = document.createElement("div");
    ov.id = "soccerOverlay";
    ov.className = "soccerOverlay hidden";
    document.body.appendChild(ov);
  }
  return ov;
}

export function showOverlay(): void {
  const ov = ensureOverlay();
  ov.classList.remove("hidden");
}

export function hideOverlay(): void {
  overlay()?.classList.add("hidden");
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");
}
